import React from "react";
import { GeneratedContent } from "@/types/content";

interface BlogPreviewProps {
  content: GeneratedContent;
}

export const BlogPreview = ({ content }: BlogPreviewProps) => {
  // Format content to handle markdown-style formatting
  const formatContent = (text: string) => {
    if (!text) return '';
    
    // Process the content in stages
    let formattedContent = text;
    
    // Convert sections with headers (# and ## and ###)
    const headerRegex = /^(#{1,3})\s+(.+)$/gm;
    formattedContent = formattedContent.replace(headerRegex, (match, hashes, title) => {
      const level = hashes.length;
      let fontSize = 'text-xl font-semibold my-3'; // Default for ###
      
      if (level === 1) {
        fontSize = 'text-3xl font-bold my-5'; // For #
      } else if (level === 2) {
        fontSize = 'text-2xl font-bold my-4'; // For ##
      }
      
      return `<div class="${fontSize}">${title}</div>`;
    });
    
    // Convert **bold** text
    const boldRegex = /\*\*([^*]+)\*\*/g;
    formattedContent = formattedContent.replace(boldRegex, '<strong>$1</strong>');
    
    // Process tables
    formattedContent = processMarkdownTables(formattedContent);
    
    // Split by newlines and wrap paragraphs
    const paragraphs = formattedContent.split('\n\n');
    return paragraphs.map((paragraph, index) => {
      // Skip wrapping if it's already an HTML element
      if (paragraph.trim().startsWith('<div') || 
          paragraph.trim().startsWith('<table') || 
          paragraph.trim() === '') {
        return <div key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />;
      }
      return <div key={index} className="my-3" dangerouslySetInnerHTML={{ __html: paragraph }} />;
    });
  };

  // Helper function to process markdown tables
  const processMarkdownTables = (text: string) => {
    // Find table sections in the markdown
    const tableRegex = /(\|.+\|\r?\n)((?:\|[-:| ]+\|\r?\n))((?:\|.+\|\r?\n)+)/g;
    
    return text.replace(tableRegex, (match, headerRow, separatorRow, bodyRows) => {
      // Process header row
      const headers = headerRow
        .trim()
        .split('|')
        .filter(cell => cell.trim() !== '')
        .map(cell => cell.trim());
      
      // Process alignment row
      const alignments = separatorRow
        .trim()
        .split('|')
        .filter(cell => cell.trim() !== '')
        .map(cell => {
          const trimmed = cell.trim();
          if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'text-center';
          if (trimmed.endsWith(':')) return 'text-right';
          return 'text-left';
        });
      
      // Process body rows
      const rows = bodyRows
        .trim()
        .split('\n')
        .map(row => 
          row
            .trim()
            .split('|')
            .filter(cell => cell.trim() !== '')
            .map(cell => cell.trim())
        );
      
      // Build HTML table
      let tableHtml = '<table class="min-w-full border-collapse my-4">\n';
      
      // Add header
      tableHtml += '<thead class="bg-gray-100 dark:bg-gray-800">\n<tr>\n';
      headers.forEach((header, i) => {
        const alignment = alignments[i] || 'text-left';
        tableHtml += `<th class="px-4 py-2 border border-gray-300 dark:border-gray-700 ${alignment}">${header}</th>\n`;
      });
      tableHtml += '</tr>\n</thead>\n';
      
      // Add body
      tableHtml += '<tbody>\n';
      rows.forEach(row => {
        tableHtml += '<tr>\n';
        row.forEach((cell, i) => {
          const alignment = alignments[i] || 'text-left';
          tableHtml += `<td class="px-4 py-2 border border-gray-300 dark:border-gray-700 ${alignment}">${cell}</td>\n`;
        });
        tableHtml += '</tr>\n';
      });
      tableHtml += '</tbody>\n</table>';
      
      return tableHtml;
    });
  };

  return (
    <div className="prose max-w-none dark:prose-invert">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold mb-6">{content.title}</h1>
        <div className="space-y-2">
          {formatContent(content.content)}
        </div>
      </div>
    </div>
  );
};
